/* tslint:disable:no-console max-line-length disable-next-line */
import {Injectable, OnDestroy} from '@angular/core';
import {Project} from '@docma-mm-core/project/project';
import {SyncState} from '@docma-mm-core/model/sync-state/sync-state';
import {SyncStatusEnum} from '@docma-mm-core/model/sync-state/sync-status.enum';
import {ConnectivityService} from '@mydocma/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, combineLatest, firstValueFrom, Subject} from 'rxjs';
import {Fault} from '../fault';
import {FaultApiService} from '../fault-api.service';
import {ProjectService} from '@mydocma/project';
import {HttpErrorResponse} from '@angular/common/http';
import {FaultPatchService, faultUidsOfPatches} from '../patch/fault-patch-service';
import {concatMap, debounceTime, distinctUntilChanged, filter, map} from 'rxjs/operators';
import {FaultPersistenceService} from '../fault-persistence.service';
import {FaultPatch} from '../patch/fault-patch';
import {AttachmentSyncService} from '@docma-mm-core/attachment/attachment-sync.service';
import {FaultSyncQueuedItem} from './fault-sync-queued-item';
import {ActivitySyncService} from '@docma-mm-core/activity/sync/activity-sync.service';
import {MmPlanEditService} from '../../../mm-faults/mm-plans-view/mm-plan-view/services/mm-plan-edit.service';
import {MmProjectDbCollections} from '../../../mm-db/mm-project-db-schema';
import {GroupSyncService} from '@docma-mm-core/group/group-sync.service';
import {MmProjectService} from '../../../mm-project/mm-project.service';
import {CurrentFaultsSyncStateService} from '../../../mm-faults/current-faults-sync-state.service';
import {PersonalizationService} from '@docma-mm-core/personalization/personalization.service';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {SyncResult} from '@docma-mm-core/model/sync-state/sync-result';
import {Logger} from '@mydocma/common/src/lib/logger';
import {ErrorService} from '../../../mm-faults/mm-error.service';
import {BaseFaultSyncService} from '@docma-mm-core/fault/sync/base-fault-sync.service';
import {PlatformModeStateService} from '../../../mm-offline/services/platform-mode-state.service';
import { BlockingDialogService } from '../../../mm-faults/blocking-dialog.service';
import { MmTranslateService } from '../../../mm-translate.service';

export interface SyncStateChange {
  faultUid: string;
  faultId: number;
  groupUid?: string;
  attachmentUid?: string;
  mydocmaUid: string;
  syncState: SyncState;
}

@UntilDestroy()
@Injectable()
export class FaultSyncService extends BaseFaultSyncService implements OnDestroy {

  constructor(protected readonly projectService: ProjectService,
              protected readonly mmProjectService: MmProjectService,
              protected readonly faultApiService: FaultApiService,
              protected readonly patchService: FaultPatchService,
              protected readonly persistenceService: FaultPersistenceService,
              protected readonly groupSyncService: GroupSyncService,
              protected readonly activitySyncService: ActivitySyncService,
              protected readonly attachmetSyncService: AttachmentSyncService,
              protected readonly planEditService: MmPlanEditService,
              protected readonly connectivityService: ConnectivityService,
              protected readonly logger: Logger,
              protected readonly errorService: ErrorService,
              private readonly currentFaultsSyncStateService: CurrentFaultsSyncStateService,
              private readonly personalizationService: PersonalizationService,
              private readonly platformModeStateService: PlatformModeStateService,
              private readonly blockingDialogService: BlockingDialogService,
              private readonly translateService: MmTranslateService) {

    super(projectService, mmProjectService, faultApiService, patchService, persistenceService, groupSyncService, activitySyncService, attachmetSyncService, planEditService, connectivityService, logger, errorService);

    this.initQueueSubscriptions();
    this.initAutoSyncSubscription();

    this.mmProjectService.currentProject
      .pipe(untilDestroyed(this))
      .subscribe(p => {
        this.syncProjectSubject.next(p ? {MyDocmaUid: p?.MyDocmaUid} : null);
      });
  }

  private readonly synFaultDebounceMilliseconds = 250;
  syncProjectSubject = new BehaviorSubject<Project>((null));

  private syncingProject: Project;

  private startSyncSubject = new Subject<boolean>();

  private syncFaultQueue =
    new BehaviorSubject<{ items: FaultSyncQueuedItem[], emit: boolean, startingFaultUids: string[], notifyError: boolean }>({items: [], emit: true, startingFaultUids: [], notifyError: false});


  // private syncingFaults: string[] = [];
  // private errorFaultSyncQueuedItems: FaultSyncQueuedItem[] = [];

  public startSync() {
    this.startSyncSubject.next(true);
  }

  public async syncFault(project: Project, faultUid: string): Promise<SyncResult> {
    const isOffline = await firstValueFrom(this.connectivityService.isOffline);

    const syncAction = async () => {
      const items: FaultSyncQueuedItem[] = [];

      const allPatches: FaultPatch[] = await this.patchService.getAll(project.MyDocmaUid);
      const patchesByFuid = this.patchService.groupByFuid(allPatches);

      // 1. check if fault has not synced changes
      if (patchesByFuid.hasOwnProperty(faultUid)) {
        this.syncingSubject.next(true);
        const patches = patchesByFuid[faultUid];
        const patchesByGuid = this.patchService.groupByGuid(allPatches);

        patches.filter(x => !!x.gUid).forEach(p => {
          if (patchesByGuid.hasOwnProperty(p.gUid)) {
            const groupPatches = patchesByGuid[p.gUid].filter(x => x != p);
            patches.push(...groupPatches);
          }
        });

        if (patches && patches.length > 0) {
          for (const patch of patches) {
            const patchItem: FaultSyncQueuedItem = {
              mydocmaUid: project.MyDocmaUid,
              date: patch.date,
              faultUid: patch.fUid,
              groupUid: patch.gUid,
              id: patch.operation.id,
              op: patch.operation.op,
              path: patch.operation.path,
            };
            items.push(patchItem);
          }

          const startByDialog = false;
          const result = await this.sync(this.syncingProject, {items, emit: true, startingFaultUids: null, notifyError: startByDialog});

          if ((result.finishedFaultSyncQueuedItems?.length ?? 0) > 0) {
            this.removeItemsFromFaultQueue(result.finishedFaultSyncQueuedItems, false);
          }

          this.personalizationService.setProjectValue(this.syncingProject.MyDocmaUid, PersonalizationKeysEnum.SyncResult, result.syncResult);
          this.syncingSubject.next(false);

        }
      }
    };

    if (isOffline) {
      // offline
      this.syncingSubject.next(false);
    } else {
      // online
      if (!project) {
        // stop sync if syncing
        this.syncingSubject.next(false);
      } else {
        if (this.syncingSubject.value) {
          if (this.syncingProject.MyDocmaUid !== project.MyDocmaUid) {
            // stop syncing previos project
            this.syncingProject = project;
            this.syncingSubject.next(false);
            await syncAction();
          }
        } else {
          this.syncingProject = project;
          await syncAction();
        }
      }
    }

    // 2. wait until all running sync jobs are finished, suspend synchronization from queue
    // 3. sync fault chages
    // 4. resume synchronization from queue

    return new SyncResult({syncState: new SyncState({status: SyncStatusEnum.Synced})});
  }

  public async syncAll() {
    const isOffline = await firstValueFrom(this.connectivityService.isOffline);
    const project = await firstValueFrom(this.mmProjectService.currentProject);

    const syncAction = async () => {
      try {
        this.syncingSubject.next(true);
        this.syncingAllSubject.next(true);

        const items: FaultSyncQueuedItem[] = [];
        const allPatches: FaultPatch[] = await this.patchService.getAll(project.MyDocmaUid);

        // 1. check if fault has not synced changes
        if (allPatches && allPatches.length > 0) {
          for (const patch of allPatches) {
            const patchItem: FaultSyncQueuedItem = {
              mydocmaUid: project.MyDocmaUid,
              date: patch.date,
              faultUid: patch.fUid,
              groupUid: patch.gUid,
              id: patch.operation.id,
              op: patch.operation.op,
              path: patch.operation.path,
            };
            items.push(patchItem);
          }

          const startByDialog = false;
          const result = await this.sync(this.syncingProject, {items, emit: true, startingFaultUids: null, notifyError: startByDialog});

          if ((result.finishedFaultSyncQueuedItems?.length ?? 0) > 0) {
            this.removeItemsFromFaultQueue(result.finishedFaultSyncQueuedItems, false);
          }

          this.personalizationService.setProjectValue(this.syncingProject.MyDocmaUid, PersonalizationKeysEnum.SyncResult, result.syncResult);
          return result;
        }

      }
      catch (ex) {
        this.logger.error(ex, '', 'FaultSyncService');
      }
      finally {
        this.syncingSubject.next(false);
        this.syncingAllSubject.next(false);
      }
    };

    if (isOffline) {
      // offline
      this.syncingSubject.next(false);
    } else {
      // online
      if (!project) {
        // stop sync if syncing
        this.syncingSubject.next(false);
      } else {
        if (this.syncingSubject.value) {
          if (this.syncingProject.MyDocmaUid !== project.MyDocmaUid) {
            // stop syncing previos project
            this.syncingProject = project;
            this.syncingSubject.next(false);
            return await syncAction();
          }
        } else {
          this.syncingProject = project;
          return await syncAction();
        }
      }
    }
  }

  private async syncProject(project: Project, startByDialog: boolean) {
    const allPatches: FaultPatch[] = await this.patchService.getAll(project.MyDocmaUid);
    const items: FaultSyncQueuedItem[] = [];

    if (allPatches && allPatches.length > 0) {
      for (const patch of allPatches) {
        const patchItem: FaultSyncQueuedItem = {
          mydocmaUid: project.MyDocmaUid,
          date: patch.date,
          faultUid: patch.fUid,
          groupUid: patch.gUid,
          id: patch.operation.id,
          op: patch.operation.op,
          path: patch.operation.path,
        };
        items.push(patchItem);
      }
      this.syncFaultQueue.next({items, emit: true, startingFaultUids: null, notifyError: startByDialog});
    }
  }

  private initAutoSyncSubscription() {
    combineLatest(
      [
        this.connectivityService.isOffline,
        this.syncProjectSubject,
        this.syncingSubject
      ]
    )
      .pipe(untilDestroyed(this))
      .subscribe(([offline, project, syncing]) => {
        this.currentFaultsSyncStateService.set(project?.MyDocmaUid, syncing);
      });

    this.startSyncSubject
      .pipe(untilDestroyed(this))
      .subscribe(async start => {
        const isOffline = await firstValueFrom(this.connectivityService.isOffline);
        const project = await firstValueFrom(this.syncProjectSubject);
        const isSyncingAll = await firstValueFrom(this.syncingAllSubject);
        this.startProjectSync(isOffline, isSyncingAll, project);
      });

    combineLatest([this.connectivityService.isOffline, this.syncProjectSubject])
      .pipe(untilDestroyed(this))
      .subscribe(async ([isOffline, project])  => {
        const isSyncingAll = await firstValueFrom(this.syncingAllSubject);
        this.startProjectSync(isOffline, isSyncingAll, project);
      });
  }

  private startProjectSync(isOffline: boolean, isSyncingAll: boolean, project: Project) {
    if (isSyncingAll) {
      return;
    }
    else if (isOffline) {
      // offline
      this.syncingSubject.next(false);
    } else {
      // online
      if (!project) {
        // stop sync if syncing
        this.syncingSubject.next(false);
      } else {
        if (this.syncingSubject.value) {
          if (this.syncingProject.MyDocmaUid !== project.MyDocmaUid) {
            // stop syncing previos project
            this.syncingProject = project;
            this.syncingSubject.next(false);
            this.syncProject(project, true);
          }
        } else {
          this.syncingProject = project;
          this.syncProject(project, true);
        }
      }
    }
  }

  private removeItemsFromFaultQueue(items: FaultSyncQueuedItem[], emit: boolean = true) {
    const allitems = [...this.syncFaultQueue.value.items];

    for (const it of items) {
      const foundIndex = allitems.findIndex(i => i.id === it.id);
      if (foundIndex > -1) {
        allitems.splice(foundIndex, 1);
      }
    }
    this.syncFaultQueue.next({items: allitems, emit, startingFaultUids: null, notifyError: false});
  }

  private initQueueSubscriptions(): void {
    const isOnline$ = this.connectivityService.isOffline
      .pipe(
        distinctUntilChanged(),
        map(isOffline => !isOffline),
      );

    const debouncedFaultQueue$ = this.syncFaultQueue.pipe(
      debounceTime(this.synFaultDebounceMilliseconds),
    );

    combineLatest([isOnline$, debouncedFaultQueue$, this.platformModeStateService.canUploadFaults$])
      .pipe(
        filter(([isOnline, _, canUploadFaults]) => isOnline && canUploadFaults),
        concatMap(async ([isOnline, queuedFaults]) => {
          const isSyncingAll = await firstValueFrom(this.syncingAllSubject);

          if (queuedFaults.items.length > 0 && queuedFaults.emit && !isSyncingAll) {
            const allKeys = await (await this.patchService.getAllKeys(queuedFaults.items[0].mydocmaUid)).map(m => m.toString());

            // if (!this.continueSyncQueue) {
            //   return;
            // }

            const obsoleteItems = queuedFaults.items.filter(f => allKeys.findIndex(k => k === f.id) === -1);
            if (obsoleteItems && obsoleteItems.length > 0) {
              /* patches are in queue witch are not existing anymore... */
              this.removeItemsFromFaultQueue(obsoleteItems);
              return;
            }

            // const i = 1;
            // if (i === 1) {
            //   // const project = await firstValueFrom(this.syncProjectSubject);
            //   // await this.syncFault(project, '014bcd6c-4a3d-444b-cb33-ec6e0aaa2827');
            //   return;
            // }

            const uniqueFaultIds = new Set(queuedFaults.items.map(i => i.faultUid));
            if (this.platformModeStateService.isLocalModeEnabled && uniqueFaultIds.size > 1){
              this.blockingDialogService.show(await firstValueFrom(this.translateService.get('MM.syncFaults.syncBlockingDialog.inProgressHeader')),
                                      await firstValueFrom(this.translateService.get('MM.syncFaults.syncBlockingDialog.inProgressBody')),
                                      queuedFaults?.items.length,
                                      false,
                                      await firstValueFrom(this.translateService.get('MM.syncFaults.syncBlockingDialog.inProgressText')),
                                      null);
            }

            const result = await this.sync(this.syncingProject, queuedFaults);

            if ((result.finishedFaultSyncQueuedItems?.length ?? 0) > 0) {
              this.removeItemsFromFaultQueue(result.finishedFaultSyncQueuedItems, false);
            }

            if (this.platformModeStateService.isLocalModeEnabled){
              this.blockingDialogService?.setProcessedFiles(1);
              const finishedFaultUids = result.finishedFaultSyncQueuedItems?.map(i => i.faultUid) ?? [];
              this.autoSyncFinishedSubject.next(finishedFaultUids);
            }

            this.personalizationService.setProjectValue(this.syncingProject.MyDocmaUid, PersonalizationKeysEnum.SyncResult, result.syncResult);
            this.syncingSubject.next(false);
          } else {
            this.syncingSubject.next(false);
          }
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  ngOnDestroy() {
  }

  addPatchesToSyncQueue(mydocmaUid: string, patches: FaultPatch[]) {
    const startingFaultUids = faultUidsOfPatches(patches);
    const items = [...this.syncFaultQueue.value.items];

    patches.forEach(x => {
      if (items.findIndex(i => i.id === x.operation.id) === -1) {
        items.push({
          mydocmaUid,
          date: x.date,
          faultUid: x.fUid,
          groupUid: x.gUid,
          id: x.operation.id,
          op: x.operation.op,
          path: x.operation.path,
        });
      }
    });

    if (!this.connectivityService.isOfflineVal) {
      this.syncFaultQueue.next({items, emit: true, startingFaultUids, notifyError: false});
    }
  }

  async addFaultToSyncQueue(mydocmaUid: string, fault: Fault) {
    const faultPatches = await this.patchService.getByFaultUid(mydocmaUid, fault.uid);
    this.addPatchesToSyncQueue(mydocmaUid, faultPatches);
  }

  private startSyncProject(project: Project) {
    this.syncProjectSubject.next(project);
  }

  private stopSyncProject(project: Project) {
    this.syncProjectSubject.next(null);
  }

  private getSyncObjects(patches: FaultPatch[]): ({ syncUid: string, dbCollectionName: string })[] {
    const syncObjects: ({ syncUid: string, dbCollectionName: string })[] = [];

    for (const p of patches) {
      if (!syncObjects.find(x => x.syncUid === p.fUid)) {
        syncObjects.push({syncUid: p.fUid, dbCollectionName: MmProjectDbCollections.faults});

      }
      if (p.gUid != null && !syncObjects.find(x => x.syncUid === p.gUid)) {
        syncObjects.push({syncUid: p.gUid, dbCollectionName: MmProjectDbCollections.groups});
      }
      // if(p.oUid != null && syncObjects.find(x => x === p.oUid)) {
      //   syncObjects.push(p.oUid);
      // }
    }
    return syncObjects;
  }


  private getSyncError(error) {
    if (error instanceof HttpErrorResponse) {
      return error.message;
    } else {
      return error;
    }
  }

  /* NOT IN USE */
  // private async syncGroupChangeState(faultId: number, changeState: ChangeState<FaultData>,
  //                                    loadState?: LoadState<FaultData>): Promise<LoadState<FaultData>> {
  //   let savedFaultData: FaultData;
  //   if (changeState.status === ChangeStatusEnum.New) {
  //     const newFault = changeState.data;
  //     savedFaultData = await this.faultApiService.createFault(newFault).toPromise();
  //     savedFaultData.Files = null;
  //     savedFaultData.Activities = null;
  //     faultId = savedFaultData.Id;
  //   }
  //
  //   return new LoadState({ status: LoadStatusEnum.Loaded, data: savedFaultData });
  // }
}




